// Simple client-side notification dispatcher that respects per-type toggles and quiet hours
// Settings source priority: explicit passed settings -> localStorage('push_settings') -> defaults

const DEFAULT_SETTINGS = {
  enabled: true,
  new_jobs: true,
  job_updates: true,
  application_status: true,
  shift_reminders: true,
  payment_notifications: true,
  promotional: false,
  quiet_hours: { enabled: false, start_time: '22:00', end_time: '08:00' },
};

function getSettings(overrides) {
  if (overrides) return { ...DEFAULT_SETTINGS, ...overrides };
  try {
    const raw = localStorage.getItem('push_settings');
    if (raw) return { ...DEFAULT_SETTINGS, ...JSON.parse(raw) };
  } catch (_) {}
  return { ...DEFAULT_SETTINGS };
}

function withinQuietHours(quiet) {
  if (!quiet?.enabled) return false;
  try {
    const now = new Date();
    const [sh, sm] = (quiet.start_time || '22:00').split(':').map((v) => parseInt(v, 10));
    const [eh, em] = (quiet.end_time || '08:00').split(':').map((v) => parseInt(v, 10));

    const start = new Date(now);
    start.setHours(sh, sm || 0, 0, 0);
    const end = new Date(now);
    end.setHours(eh, em || 0, 0, 0);

    if (start <= end) {
      // same-day window
      return now >= start && now <= end;
    }
    // crosses midnight
    return now >= start || now <= end;
  } catch (_) {
    return false;
  }
}

function supportsNotification() {
  return typeof window !== 'undefined' && 'Notification' in window;
}

function showBrowserNotification(title, options) {
  try {
    // Some browsers require document visibility handling; for now, just create
    return new Notification(title, options);
  } catch (e) {
    // Fallback: no-op
    return null;
  }
}

// Map type to a default title/body builder
const TYPE_META = {
  new_jobs: (payload) => ({
    title: '新着求人',
    body: payload?.text || 'あなたの条件に合う新しい求人があります',
  }),
  job_updates: (payload) => ({
    title: '求人情報の更新',
    body: payload?.text || 'お気に入りの求人が更新されました',
  }),
  application_status: (payload) => ({
    title: '応募状況の変更',
    body: payload?.text || '応募した求人の状況が更新されました',
  }),
  shift_reminders: (payload) => ({
    title: 'シフトリマインダー',
    body: payload?.text || 'まもなく勤務開始です',
  }),
  payment_notifications: (payload) => ({
    title: '入金のお知らせ',
    body: payload?.text || 'ウォレットに報酬が入金されました',
  }),
  promotional: (payload) => ({
    title: 'お知らせ',
    body: payload?.text || '最新のキャンペーン・アップデート',
  }),
};

export const NotificationService = {
  isSupported() {
    return supportsNotification();
  },
  permission() {
    if (!supportsNotification()) return 'denied';
    return Notification.permission;
  },
  async requestPermission() {
    if (!supportsNotification()) return 'denied';
    return await Notification.requestPermission();
  },
  init() {
    // Listen global app events: window.dispatchEvent(new CustomEvent('notify', { detail: { type, payload } }))
    if (typeof window !== 'undefined' && !window.__bytersNotifyInit) {
      window.addEventListener('notify', (ev) => {
        try {
          const { type, payload } = ev.detail || {};
          if (!type) return;
          NotificationService.trigger(type, payload);
        } catch (_) {}
      });
      window.__bytersNotifyInit = true;
    }
  },
  attachAxios(axiosInstance) {
    if (!axiosInstance || axiosInstance.__bytersNotifyAxios) return;
    axiosInstance.interceptors.response.use((response) => {
      try {
        const url = (response?.config?.url || '').toLowerCase();
        const method = (response?.config?.method || 'get').toLowerCase();
        // Heuristics to trigger notifications by API actions
        if (method === 'post' && url.includes('/applications')) {
          NotificationService.trigger('application_status', { text: '応募を送信しました' });
        }
        if (url.includes('/wallet') && (method === 'post' || method === 'patch' || method === 'put')) {
          NotificationService.trigger('payment_notifications', { text: 'ウォレット更新が完了しました' });
        }
        if (url.includes('/jobs/') && (method === 'put' || method === 'patch')) {
          NotificationService.trigger('job_updates', { text: '求人情報が更新されました' });
        }
      } catch (_) {}
      return response;
    }, (error) => Promise.reject(error));
    axiosInstance.__bytersNotifyAxios = true;
  },
  trigger(type, payload = {}, overrides = null) {
    const settings = getSettings(overrides);
    if (!supportsNotification()) return { ok: false, reason: 'unsupported' };
    if (Notification.permission !== 'granted') return { ok: false, reason: 'not_granted' };
    if (!settings.enabled) return { ok: false, reason: 'disabled_all' };
    if (!settings[type]) return { ok: false, reason: 'disabled_type' };
    if (withinQuietHours(settings.quiet_hours)) return { ok: false, reason: 'quiet_hours' };

    const metaBuilder = TYPE_META[type] || ((p) => ({ title: '通知', body: p?.text || '' }));
    const { title, body } = metaBuilder(payload);
    showBrowserNotification(title, {
      body,
      icon: '/favicon.ico',
    });
    return { ok: true };
  },
};
